import { useState, useEffect } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X, TrendingUp, Loader2, ChevronRight, UploadCloud, FileText, Undo2, History, Trash2 } from 'lucide-react';
import PricingProductTable from './PricingProductTable';
import { SupplierActiveToggle } from './PricingActiveToggle';
import PriceListReaderModal from './PriceListReaderModal';
import CleanUpCategoriesModal from './CleanUpCategoriesModal';

const enconOrange = '#ee7203';

/** Add-rebate-or-discount popover shown next to each category ("product group"),
 * per the "next to each product group there will be rebate and discount data,
 * an add button will show clicking that will give the user an option to add a
 * rebate or a discount" requirement. */
const AddRateButton = ({ category, onAdded }) => {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState(null); // 'rebate' | 'discount'
  const [label, setLabel] = useState('');
  const [pct, setPct] = useState('');

  const submit = async () => {
    if (!label.trim() || !pct) { toast.error('Label and % are required'); return; }
    try {
      const path = kind === 'rebate' ? 'rebates' : 'discounts';
      await api.post(`${API}/pricing/categories/${category.id}/${path}`, { label, value_pct: parseFloat(pct) });
      toast.success(`${kind === 'rebate' ? 'Rebate' : 'Discount'} added`);
      setOpen(false); setKind(null); setLabel(''); setPct('');
      onAdded();
    } catch {
      toast.error('Failed to add');
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setKind(null); }}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className="p-1 rounded hover:bg-orange-100 text-orange-600"
          title="Add rebate or discount"
          data-testid={`pricing-add-rate-btn-${category.id}`}
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="bg-white w-64" onClick={(e) => e.stopPropagation()}>
        {!kind ? (
          <div className="flex flex-col gap-2">
            <Button size="sm" variant="outline" onClick={() => setKind('rebate')} data-testid="pricing-choose-rebate">Add Rebate</Button>
            <Button size="sm" variant="outline" onClick={() => setKind('discount')} data-testid="pricing-choose-discount">Add Discount</Button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-600">{kind === 'rebate' ? 'New Rebate' : 'New Discount'}</p>
            <Input placeholder="Label (e.g. Annual Rebate)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="pricing-rate-label-input" />
            <Input type="number" placeholder="%" value={pct} onChange={(e) => setPct(e.target.value)} data-testid="pricing-rate-pct-input" />
            <Button size="sm" className="w-full text-white" style={{ backgroundColor: enconOrange }} onClick={submit} data-testid="pricing-rate-submit-btn">Add</Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const IncreaseDialog = ({ open, onClose, onSubmit, title }) => {
  const [pct, setPct] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-sm" data-testid="pricing-increase-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-gray-600">Percentage Increase (%)</Label>
            <Input type="number" step="0.1" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="e.g. 5" data-testid="pricing-increase-pct-input" />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Effective / Update Date</Label>
            <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} data-testid="pricing-increase-date-input" />
            <p className="text-[11px] text-gray-400 mt-1">Today or earlier applies immediately. A future date is scheduled — see "Scheduled Increases" tab.</p>
          </div>
          <div>
            <Label className="text-xs text-gray-600">Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="pricing-increase-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            style={{ backgroundColor: enconOrange }}
            className="text-white"
            onClick={() => { onSubmit({ pct: parseFloat(pct) || 0, effective_date: effectiveDate, note }); }}
            data-testid="pricing-increase-submit-btn"
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// eslint-disable-next-line no-unused-vars
const LegacyUpdatePriceListPdfModal = ({ supplier, open, onClose, onUpdated }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null); // { completed, total }

  const pollJob = (jobId) => {
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`${API}/pricing/pdf-import-jobs/${jobId}`);
        const job = res.data;
        setProgress({ completed: job.completed_chunks || 0, total: job.total_chunks || 0 });
        if (job.status === 'done') {
          clearInterval(interval);
          toast.success(job.result?.message || 'Price list processed');
          setFile(null); setProgress(null); setUploading(false);
          onUpdated();
          onClose();
        } else if (job.status === 'failed') {
          clearInterval(interval);
          toast.error(job.error || 'Failed to process this PDF');
          setProgress(null); setUploading(false);
        }
      } catch {
        clearInterval(interval);
        toast.error('Lost connection while processing — check Settings > Pricing for the result');
        setProgress(null); setUploading(false);
      }
    }, 2000);
  };

  const submit = async () => {
    if (!file) { toast.error('Choose a PDF first'); return; }
    setUploading(true);
    setProgress({ completed: 0, total: 0 });
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`${API}/pricing/suppliers/${supplier.id}/upload-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.info('Reading and matching your price list — this can take a minute for large catalogs');
      pollJob(res.data.job_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to process this PDF');
      setUploading(false);
      setProgress(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !uploading) { setFile(null); onClose(); } }}>
      <DialogContent className="bg-white max-w-md" data-testid="pricing-pdf-update-dialog">
        <DialogHeader><DialogTitle>Update Price List — {supplier.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-gray-500">
          Upload a {supplier.name} price list or quote PDF (any length — multi-page catalogs are read in
          full). AI reads the product codes and prices and updates any matching products in this supplier —
          new codes are added under "Uncategorised (PDF Import)" for you to re-file.
        </p>
        <label
          className="border-2 border-dashed rounded-xl p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-orange-300 transition-colors"
          data-testid="pricing-pdf-update-dropzone"
        >
          <input
            type="file"
            accept="application/pdf"
            className="hidden"
            disabled={uploading}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="pricing-pdf-update-file-input"
          />
          {file ? (
            <><FileText className="w-6 h-6 text-orange-500" /><span className="text-sm font-medium">{file.name}</span></>
          ) : (
            <><UploadCloud className="w-6 h-6 text-gray-400" /><span className="text-sm text-gray-500">Click to choose a PDF</span></>
          )}
        </label>
        {progress && (
          <p className="text-xs text-gray-500" data-testid="pricing-pdf-update-progress">
            {progress.total > 0
              ? `Reading page batch ${progress.completed}/${progress.total}...`
              : 'Starting...'}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={uploading}>Cancel</Button>
          <Button
            style={{ backgroundColor: enconOrange }}
            className="text-white"
            onClick={submit}
            disabled={!file || uploading}
            data-testid="pricing-pdf-update-submit-btn"
          >
            {uploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Processing...</> : 'Update Prices'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const UpdateDrywallCalculatorModal = ({ supplier, open, onClose, onUpdated }) => {
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState('');
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    if (!file) { toast.error('Choose a file first'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (password) formData.append('password', password);
      const res = await api.post(`${API}/pricing/suppliers/${supplier.id}/upload-drywall-calculator`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data?.message || 'Drywall Calculator processed');
      setFile(null); setPassword('');
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to process this file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setFile(null); setPassword(''); onClose(); } }}>
      <DialogContent className="bg-white max-w-md" data-testid="pricing-drywall-calc-dialog">
        <DialogHeader><DialogTitle>Upload Drywall Calculator — {supplier.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-gray-500">
          Upload the manufacturer's Contribution Calculator (BG/Knauf/Siniat-style .xlsm).
          List Price, Off List Discount % and Total Supplier Rebate % are read straight from
          the sheet and applied — rebates/discounts are added per product group automatically.
        </p>
        <label
          className="border-2 border-dashed rounded-xl p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-orange-300 transition-colors"
          data-testid="pricing-drywall-calc-dropzone"
        >
          <input
            type="file"
            accept=".xlsm,.xlsx"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="pricing-drywall-calc-file-input"
          />
          {file ? (
            <><FileText className="w-6 h-6 text-orange-500" /><span className="text-sm font-medium">{file.name}</span></>
          ) : (
            <><UploadCloud className="w-6 h-6 text-gray-400" /><span className="text-sm text-gray-500">Click to choose an .xlsm / .xlsx file</span></>
          )}
        </label>
        <div>
          <Label className="text-xs text-gray-600">Password (if protected)</Label>
          <Input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="e.g. BRIT01"
            className="mt-1"
            data-testid="pricing-drywall-calc-password-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={uploading}>Cancel</Button>
          <Button
            style={{ backgroundColor: enconOrange }}
            className="text-white"
            onClick={submit}
            disabled={!file || uploading}
            data-testid="pricing-drywall-calc-submit-btn"
          >
            {uploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Processing...</> : 'Upload & Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const HistoryModal = ({ supplier, open, onClose, onReverted }) => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revertingId, setRevertingId] = useState(null);
  const [confirmBatch, setConfirmBatch] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/pricing/suppliers/${supplier.id}/batches`);
      setBatches(res.data?.batches || []);
    } catch {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open, supplier.id]);

  const typeLabel = (type) => ({
    increase: 'Price Increase',
    manual_edit: 'Manual Edit',
    excel_import: 'Excel Import',
    pdf_import: 'PDF Import',
    drywall_calculator_import: 'Drywall Calculator Import',
  }[type] || type);

  const doRevert = async (batch) => {
    setRevertingId(batch.id);
    try {
      const res = await api.post(`${API}/pricing/batches/${batch.id}/revert`);
      toast.success(`Reverted — ${res.data.restored_count} price(s) restored${res.data.deleted_count ? `, ${res.data.deleted_count} duplicate(s) removed` : ''}`);
      setConfirmBatch(null);
      load();
      onReverted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to revert');
    } finally {
      setRevertingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="pricing-history-dialog">
        <DialogHeader><DialogTitle>Price History — {supplier.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-gray-500">
          Every upload, % increase, or manual edit is logged here. Revert undoes a bad upload — it
          restores prices to what they were before AND removes any duplicate products that
          upload created. Category-level rebate/discount % is not affected.
        </p>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: enconOrange }} /></div>
        ) : batches.length === 0 ? (
          <p className="text-center text-gray-400 py-8">No price changes recorded yet for this supplier.</p>
        ) : (
          <div className="space-y-2">
            {batches.map((b) => (
              <div key={b.id} className="flex items-center justify-between p-3 rounded-xl border" data-testid={`pricing-history-row-${b.id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-orange-50 text-orange-700">{typeLabel(b.type)}</Badge>
                    <span className="text-sm font-medium">{b.label}</span>
                    {b.reverted && <Badge variant="secondary" className="bg-gray-100 text-gray-500">Reverted</Badge>}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(b.created_at).toLocaleString()} · {b.product_count} product{b.product_count === 1 ? '' : 's'} · {b.created_by}
                  </p>
                </div>
                {!b.reverted && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => setConfirmBatch(b)}
                    disabled={revertingId === b.id}
                    data-testid={`pricing-history-revert-btn-${b.id}`}
                  >
                    {revertingId === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5 mr-1" />}
                    Revert
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="pricing-history-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={!!confirmBatch} onOpenChange={(v) => { if (!v) setConfirmBatch(null); }}>
        <DialogContent className="bg-white max-w-sm" data-testid="pricing-history-confirm-dialog">
          <DialogHeader><DialogTitle>Revert this change?</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">
            This will restore {confirmBatch?.product_count} product{confirmBatch?.product_count === 1 ? '' : 's'} to its price
            before "{confirmBatch?.label}", and remove any duplicate products it created. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmBatch(null)}>Cancel</Button>
            <Button
              className="text-white bg-red-600 hover:bg-red-700"
              onClick={() => doRevert(confirmBatch)}
              data-testid="pricing-history-confirm-revert-btn"
            >
              Yes, Revert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

const PricingCategoryList = ({ supplier, onBack }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [increaseTarget, setIncreaseTarget] = useState(null); // { type: 'supplier'|'category', category? }
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [drywallModalOpen, setDrywallModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/pricing/suppliers/${supplier.id}/categories`);
      setCategories(res.data?.categories || []);
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [supplier.id]);

  const submitNewCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await api.post(`${API}/pricing/suppliers/${supplier.id}/categories`, { name: newCategoryName });
      toast.success('Category added');
      setNewCategoryName(''); setAddingCategory(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add category');
    }
  };

  const deleteRate = async (category, kind, id) => {
    const path = kind === 'rebate' ? 'rebates' : 'discounts';
    try {
      await api.delete(`${API}/pricing/categories/${category.id}/${path}/${id}`);
      load();
    } catch {
      toast.error('Failed to remove');
    }
  };

  const toggleCategoryActive = async (cat, isActive) => {
    setCategories(prev => prev.map(c => c.id === cat.id ? { ...c, is_active: isActive } : c));
    try {
      await api.patch(`${API}/pricing/categories/${cat.id}/active`, { is_active: isActive });
      toast.success(isActive
        ? `"${cat.name}" prices now show in Create Quote / Pro Forma`
        : `"${cat.name}" prices are now hidden from Create Quote / Pro Forma`);
    } catch {
      toast.error('Failed to update');
      load(); // roll back the optimistic flip
    }
  };

  const submitIncrease = async (body) => {
    try {
      const scope = increaseTarget.type; // 'supplier' | 'category'
      const res = await api.post(`${API}/pricing/scheduled-increases`, {
        scope,
        supplier_id: supplier.id,
        category_id: scope === 'category' ? increaseTarget.category.id : null,
        pct: body.pct,
        effective_date: body.effective_date,
        note: body.note,
      });
      if (res.data.status === 'applied') {
        toast.success(`Increased ${res.data.updated_count} product${res.data.updated_count === 1 ? '' : 's'} now`);
      } else {
        toast.success(`Scheduled — will apply automatically on ${body.effective_date}. Manage it under the "Scheduled Increases" tab.`);
      }
      setIncreaseTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to apply increase');
    }
  };

  if (selectedCategory) {
    return <PricingProductTable category={selectedCategory} onBack={() => setSelectedCategory(null)} />;
  }

  const activeCount = categories.filter(c => c.is_active).length;
  const totalCount = categories.length;
  const activeStatus = totalCount === 0 || activeCount === 0 ? 'off' : activeCount === totalCount ? 'on' : 'partial';

  return (
    <div data-testid="pricing-category-list">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="pricing-categories-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Suppliers
          </Button>
          <h3 className="font-bold text-lg">{supplier.name}</h3>
          {totalCount > 0 && (
            <SupplierActiveToggle
              supplierId={supplier.id}
              supplierName={supplier.name}
              activeStatus={activeStatus}
              activeCount={activeCount}
              totalCount={totalCount}
              onChanged={load}
            />
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setCleanupOpen(true)} data-testid="pricing-supplier-cleanup-btn">
            <Trash2 className="w-4 h-4 mr-1" /> Clean Up
          </Button>
          <Button variant="outline" size="sm" onClick={() => setHistoryModalOpen(true)} data-testid="pricing-supplier-history-btn">
            <History className="w-4 h-4 mr-1" /> History
          </Button>
          <Button variant="outline" size="sm" onClick={() => setDrywallModalOpen(true)} data-testid="pricing-supplier-drywall-calc-btn">
            <UploadCloud className="w-4 h-4 mr-1" /> Upload Drywall Calculator
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPdfModalOpen(true)} data-testid="pricing-supplier-update-pdf-btn">
            <UploadCloud className="w-4 h-4 mr-1" /> Update Price List (PDF)
          </Button>
          <Button variant="outline" size="sm" onClick={() => setIncreaseTarget({ type: 'supplier' })} data-testid="pricing-supplier-increase-btn">
            <TrendingUp className="w-4 h-4 mr-1" /> Increase All Prices
          </Button>
          <Button onClick={() => setAddingCategory(true)} style={{ backgroundColor: enconOrange }} className="text-white" size="sm" data-testid="pricing-add-category-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Category
          </Button>
        </div>
      </div>

      <PriceListReaderModal
        supplier={supplier}
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        onUpdated={load}
      />
      <UpdateDrywallCalculatorModal
        supplier={supplier}
        open={drywallModalOpen}
        onClose={() => setDrywallModalOpen(false)}
        onUpdated={load}
      />
      <CleanUpCategoriesModal
        supplier={supplier}
        open={cleanupOpen}
        onClose={() => setCleanupOpen(false)}
        onDone={load}
      />
      <HistoryModal
        supplier={supplier}
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        onReverted={load}
      />

      {addingCategory && (
        <div className="mb-4 flex gap-2">
          <Input placeholder="Category name (e.g. Batts - Common)" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} data-testid="pricing-new-category-input" />
          <Button onClick={submitNewCategory} style={{ backgroundColor: enconOrange }} className="text-white" data-testid="pricing-new-category-submit">Add</Button>
          <Button variant="outline" onClick={() => setAddingCategory(false)}>Cancel</Button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" style={{ color: enconOrange }} /></div>
      ) : (
        <div className="grid gap-2">
          {categories.map(cat => (
            <div
              key={cat.id}
              className="flex items-center justify-between p-3 rounded-xl border hover:border-orange-300 hover:shadow-sm cursor-pointer transition-all"
              onClick={() => setSelectedCategory(cat)}
              data-testid={`pricing-category-row-${cat.id}`}
            >
              <div className="flex items-center gap-3">
                <span className="font-medium">{cat.name}</span>
                <span className="text-xs text-gray-400">{cat.product_count} product{cat.product_count === 1 ? '' : 's'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()} title="Show this group's prices in Create Quote / Pro Forma">
                  <Switch
                    checked={!!cat.is_active}
                    onCheckedChange={(v) => toggleCategoryActive(cat, v)}
                    data-testid={`pricing-category-active-switch-${cat.id}`}
                  />
                  <span className={`text-[10px] font-bold uppercase tracking-wide ${cat.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                    {cat.is_active ? 'Active' : 'Off'}
                  </span>
                </div>
                {(cat.rebates || []).map(r => (
                  <Badge key={r.id} variant="secondary" className="bg-blue-50 text-blue-700 gap-1">
                    {r.label} {r.value_pct}%
                    <X className="w-3 h-3 cursor-pointer" onClick={(e) => { e.stopPropagation(); deleteRate(cat, 'rebate', r.id); }} data-testid={`pricing-remove-rebate-${r.id}`} />
                  </Badge>
                ))}
                {(cat.discounts || []).map(d => (
                  <Badge key={d.id} variant="secondary" className="bg-purple-50 text-purple-700 gap-1">
                    {d.label} {d.value_pct}%
                    <X className="w-3 h-3 cursor-pointer" onClick={(e) => { e.stopPropagation(); deleteRate(cat, 'discount', d.id); }} data-testid={`pricing-remove-discount-${d.id}`} />
                  </Badge>
                ))}
                <AddRateButton category={cat} onAdded={load} />
                <button
                  onClick={(e) => { e.stopPropagation(); setIncreaseTarget({ type: 'category', category: cat }); }}
                  className="p-1 rounded hover:bg-gray-100 text-gray-500"
                  title="Apply % increase to this category"
                  data-testid={`pricing-category-increase-btn-${cat.id}`}
                >
                  <TrendingUp className="w-3.5 h-3.5" />
                </button>
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </div>
            </div>
          ))}
          {categories.length === 0 && <p className="text-center text-gray-400 py-8">No categories yet — add one above.</p>}
        </div>
      )}

      <IncreaseDialog
        open={!!increaseTarget}
        onClose={() => setIncreaseTarget(null)}
        onSubmit={submitIncrease}
        title={increaseTarget?.type === 'supplier' ? `Increase ALL prices for ${supplier.name}` : `Increase prices for ${increaseTarget?.category?.name}`}
      />
    </div>
  );
};

export default PricingCategoryList;
